import {
  Body1,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { Add16Regular, ArrowUpRight16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { AppDialog } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { FuiButton, FuiInput } from "@/components/fui-control";
import { TaskBulkBar } from "@/features/tasks/TaskBulkBar";
import { openLinkedTasks } from "@/features/tasks/links";
import { tasksLinkedTo } from "@/features/tasks/task-query";
import { AssigneeAvatars, PriorityBadge, StatusBadge, useTaskContext } from "@/features/tasks/task-ui";
import { formatDate } from "@/lib/date-format";
import { isOverdue } from "@/store/execution-task-ops";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  grow: {
    flex: 1,
    minWidth: 0,
  },
  hideDone: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  tableWrap: {
    maxHeight: "380px",
    minHeight: "160px",
    overflow: "auto",
  },
  headerCell: {
    whiteSpace: "nowrap",
  },
  cell: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  title: {
    minWidth: 0,
    overflow: "hidden",
  },
  titleText: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  due: {
    textAlign: "left",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  check: {
    width: "40px",
    boxSizing: "border-box",
    verticalAlign: "middle",
  },
  checkHead: {
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalXXS,
  },
  overdue: {
    color: tokens.colorPaletteRedForeground1,
  },
  today: {
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  row: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  selectedRow: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  done: {
    textDecoration: "line-through",
    color: tokens.colorNeutralForeground3,
  },
  unset: {
    color: tokens.colorNeutralForeground4,
  },
});

interface LinkedTasksDialogProps {
  open: boolean;
  onClose: () => void;
  scheduleTaskId: string;
}

export function LinkedTasksDialog({ open, onClose, scheduleTaskId }: LinkedTasksDialogProps) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const ctx = useTaskContext();
  const projectId = useProjectStore((s) => s.project.id);
  const project = useProjectStore((s) => s.project);
  const schedule = useProjectStore((s) => s.derived.schedule);
  const executionTasks = useProjectStore((s) => s.project.executionTasks);
  const s = useProjectStore.getState;

  const [newTitle, setNewTitle] = useState("");
  const [hideDone, setHideDone] = useState(false);
  const [selection, setSelection] = useState<string[]>([]);
  const lastClickedRef = useRef<string | null>(null);

  const schedTask = project.tasks.find((t) => t.id === scheduleTaskId);
  const sched = schedule?.tasks.get(scheduleTaskId);
  const isSummary = !!sched?.isSummary;
  const kindLabel = isSummary ? "WBS 요약" : sched?.isMilestone ? "마일스톤" : "작업";

  const linked = tasksLinkedTo(scheduleTaskId, executionTasks, schedule);
  const filteredTasks = hideDone ? linked.filter((t) => t.status !== "done") : linked;
  const visibleIds = useMemo(() => filteredTasks.map((t) => t.id), [filteredTasks]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selection.includes(id));
  const inSelectMode = selection.length > 0;
  const title = `${sched?.wbs ?? ""} ${schedTask?.name || "작업"}`.trim();

  const sheetOpen = useProjectStore((st) => !!st.executionFocusId);

  useEffect(() => {
    if (!open) {
      setSelection([]);
      setNewTitle("");
      lastClickedRef.current = null;
    }
  }, [open]);

  const linkedIds = useMemo(() => linked.map((t) => t.id).join("\0"), [linked]);
  useEffect(() => {
    const alive = new Set(linkedIds.split("\0").filter(Boolean));
    setSelection((prev) => {
      const next = prev.filter((id) => alive.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [linkedIds]);

  const toggleId = (id: string) => {
    setSelection((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    lastClickedRef.current = id;
  };

  const handleAddTask = () => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    s().addExecutionTask({
      title: trimmed,
      linkedTaskIds: [scheduleTaskId],
      start: sched?.start,
      due: sched?.finish,
    }, { open: false });
    setNewTitle("");
  };

  const handleOpenTaskTab = () => {
    onClose();
    openLinkedTasks(projectId, scheduleTaskId);
  };

  const handleRowClick = (e: MouseEvent, id: string) => {
    if (e.shiftKey) {
      const prev = lastClickedRef.current;
      const b = visibleIds.indexOf(id);
      const a = prev ? visibleIds.indexOf(prev) : -1;
      if (a >= 0 && b >= 0) {
        const [from, to] = a < b ? [a, b] : [b, a];
        setSelection(visibleIds.slice(from, to + 1));
      } else {
        setSelection([id]);
        lastClickedRef.current = id;
      }
      return;
    }
    if (e.metaKey || e.ctrlKey) {
      toggleId(id);
      return;
    }
    if (inSelectMode) {
      toggleId(id);
      return;
    }
    lastClickedRef.current = id;
    s().setExecutionFocus(id);
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      persistWhile={sheetOpen}
      modal={false}
      width="xl"
      title={`${title} · ${kindLabel}`}
      footerStart={
        <Body1>
          전체 {linked.length}개 작업
        </Body1>
      }
      footer={
        <>
          <FuiButton appearance="secondary" onClick={onClose}>
            닫기
          </FuiButton>
          <FuiButton appearance="primary" icon={<ArrowUpRight16Regular />} onClick={handleOpenTaskTab}>
            작업 탭에서 전체 보기
          </FuiButton>
        </>
      }
    >
      {inSelectMode ? (
        <TaskBulkBar compact ids={selection} onClear={() => setSelection([])} />
      ) : (
        <div className={styles.toolbar}>
          <div className={styles.grow}>
            <FuiInput
              value={newTitle}
              onChange={(_, d) => setNewTitle(d.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddTask();
                }
              }}
              placeholder="새 Task 제목 입력 후 Enter"
            />
          </div>
          <FuiButton appearance="secondary" icon={<Add16Regular />} disabled={!newTitle.trim()} onClick={handleAddTask}>
            추가
          </FuiButton>
          <div className={styles.hideDone}>
            <Checkbox
              checked={hideDone}
              onChange={(_, d) => setHideDone(!!d.checked)}
              label="완료 숨기기"
            />
          </div>
        </div>
      )}

      <div className={styles.tableWrap}>
        {filteredTasks.length === 0 ? (
          <GhostEmptyState
            compact
            title={linked.length === 0 ? "연결된 Task가 없습니다" : "표시할 미완료 작업이 없습니다"}
            description={
              linked.length === 0
                ? "위 입력창에 제목을 입력하고 추가하면 이 WBS에 연결된 Task가 생성됩니다."
                : "모든 작업이 완료되었습니다."
            }
          />
        ) : (
          <Table size="small" className={table.fluent} aria-label="연결된 Task">
            <TableHeader>
              <TableRow className={table.headRow}>
                <th className={mergeClasses(table.head, styles.headerCell, styles.check, styles.checkHead)} onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={allSelected}
                    aria-label="모두 선택"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(_, d) => setSelection(d.checked ? visibleIds : [])}
                  />
                </th>
                <TableHeaderCell className={mergeClasses(table.head, styles.headerCell)}>제목</TableHeaderCell>
                <TableHeaderCell className={mergeClasses(table.head, styles.headerCell)} style={{ width: 88 }}>상태</TableHeaderCell>
                <TableHeaderCell className={mergeClasses(table.head, styles.headerCell)} style={{ width: 80 }}>우선순위</TableHeaderCell>
                <TableHeaderCell className={mergeClasses(table.head, styles.headerCell)} style={{ width: 76 }}>담당</TableHeaderCell>
                <TableHeaderCell className={mergeClasses(table.head, styles.headerCell, styles.due)} style={{ width: 132 }}>마감일</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTasks.map((t) => {
                const isDone = t.status === "done";
                const selected = selection.includes(t.id);
                return (
                  <TableRow
                    key={t.id}
                    className={mergeClasses(styles.row, selected && styles.selectedRow)}
                    onClick={(e) => handleRowClick(e, t.id)}
                  >
                    <TableCell className={mergeClasses(styles.cell, styles.check)} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected}
                        aria-label="선택"
                        onChange={() => toggleId(t.id)}
                      />
                    </TableCell>
                    <TableCell className={mergeClasses(styles.cell, styles.title)}>
                      <Body1 className={mergeClasses(styles.titleText, isDone && styles.done)} title={t.title || "제목 없음"}>
                        {t.title || <span className={styles.unset}>제목 없음</span>}
                      </Body1>
                    </TableCell>
                    <TableCell className={styles.cell}>
                      <StatusBadge status={t.status} size="small" />
                    </TableCell>
                    <TableCell className={styles.cell}>
                      <PriorityBadge priority={t.priority} size="small" showUnset />
                    </TableCell>
                    <TableCell className={styles.cell}>
                      <AssigneeAvatars names={t.assignees} size={20} />
                    </TableCell>
                    <TableCell className={mergeClasses(styles.cell, styles.due)}>
                      {t.due ? (
                        <span
                          className={mergeClasses(
                            isOverdue(t, ctx.today) && styles.overdue,
                            t.due === ctx.today && t.status !== "done" && styles.today,
                          )}
                        >
                          {formatDate(t.due, "numeric")}
                        </span>
                      ) : (
                        <span className={styles.unset}>—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </AppDialog>
  );
}
